/** The paid steps that can be pre-authorized per domain. Mirrors the backend
 *  `ENRICHMENT_STEPS`; Top SERP rivals is deliberately not among them. */
export type EnrichmentStep =
  | 'estimated-keywords'
  | 'url-serp-snapshots'
  | 'supporting-pages-opportunities';

import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * Domain recrawl scheduling — FW-CRAWL-DELTA-01 phase 6.
 * Backend: `GET`/`PUT /api/v1/domains/:domain/crawl-schedule`.
 *
 * These types mirror `backend/src/services/domain-crawl-scheduler.ts`.
 * `statusLine` is the backend's copy and is rendered as-is: the difference
 * between "off", "paused" and "due now" is a rule that lives with the sweeper
 * that acts on it, not in a template.
 *
 * `nextCrawlAt` is an absolute UTC instant and is the **only** thing this UI
 * formats itself, through Angular's `date` pipe, which renders it in the
 * viewer's timezone. The backend deliberately never sends a wall-clock string.
 */
export type CrawlScheduleFrequency = 'manual' | 'weekly' | 'monthly';
export type CrawlScheduleStatus = 'off' | 'paused' | 'scheduled' | 'due' | 'failing';

export interface CrawlScheduleLastCrawl {
  crawlRunId: string;
  status: string;
  startedAt: string | null;
  completedAt: string | null;
  source: 'scheduled' | 'manual';
}

export interface CrawlSchedule {
  scopeDomain: string;
  configured: boolean;
  enabled: boolean;
  frequency: CrawlScheduleFrequency;
  nextCrawlAt: string | null;
  crawlOptions: { crawlMode: 'sitemap' | 'curated' | 'domain'; limit: number };
  /** Standing authorization for the paid enrichment block on future crawls. */
  autoEnrichments?: { enabled: boolean; steps: EnrichmentStep[]; consentedAt?: string | null };
  lastEnqueuedAt: string | null;
  lastCrawlRunId: string | null;
  lastAttemptAt: string | null;
  lastError: string | null;
  consecutiveFailures: number;
  crawlInProgress: boolean;
  lastCrawl: CrawlScheduleLastCrawl | null;
  status: CrawlScheduleStatus;
  statusLine: string;
  /** A duration, not a date — "in 6 days", "due now (2 days ago)". */
  nextCrawlLabel: string | null;
  overdue: boolean;
}

export interface CrawlSchedulePatch {
  frequency?: CrawlScheduleFrequency;
  enabled?: boolean;
  /** ISO 8601. `null` drops the override and re-derives the time from the
   *  cadence — a different request from leaving the field out. */
  nextCrawlAt?: string | null;
  limit?: number;
  crawlMode?: 'sitemap' | 'curated' | 'domain';
  autoEnrichments?: { enabled: boolean; steps: EnrichmentStep[] };
}

@Injectable({ providedIn: 'root' })
export class CrawlScheduleService {
  private readonly API_URL = environment.apiUrl + '/v1';

  constructor(private http: HttpClient) {}

  getForDomain(domain: string): Observable<{ success: boolean; data: CrawlSchedule }> {
    return this.http.get<{ success: boolean; data: CrawlSchedule }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl-schedule`
    );
  }

  /**
   * Start a full crawl of the domain's competitive set — focal + every marked
   * competitor, then the authorized paid legs. `POST /crawl/start` is the
   * single-domain fetch; this is what a crawl means in the product.
   */
  startFullCycle(
    domain: string,
    body: { enrichments: EnrichmentStep[]; rivals: boolean }
  ): Observable<{ success: boolean; data: unknown }> {
    return this.http.post<{ success: boolean; data: unknown }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl-cycle`,
      body
    );
  }

  update(domain: string, patch: CrawlSchedulePatch): Observable<{ success: boolean; data: CrawlSchedule }> {
    return this.http.put<{ success: boolean; data: CrawlSchedule }>(
      `${this.API_URL}/domains/${encodeURIComponent(domain)}/crawl-schedule`,
      patch
    );
  }
}
